import { useCallback, useEffect, useState } from "react";
import { File as GQLFile } from "src/graphql/generated/graphql";
import { duplicateImageMutation } from "src/graphql/operations/files/mutations";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";
import { handleError } from "src/utils/error";

export const useDuplicateImage = (id?: string) => {
    const [isDuplicatingImage, setIsDuplicatingImage] = useState(!!id);
    const [duplicatedImage, setDuplicatedImage] = useState<GQLFile | undefined | null>(undefined);

    const duplicateImage = useCallback(
        async (imageId: string = id): Promise<GQLFile> => {
            try {
                const response = await graphqlFetch(duplicateImageMutation, {
                    id: imageId,
                });

                return response.duplicateImage;
            } catch (err) {
                handleError(err, {
                    message: { key: "error_duplicating_image", namespace: "common" },
                    context: { imageId, id },
                });
            }
        },
        [id]
    );

    const duplicateImageLoading = useCallback(async () => {
        if (isDuplicatingImage) {
            return;
        }

        setIsDuplicatingImage(true);
        const result = await duplicateImage(id);
        setIsDuplicatingImage(false);
        setDuplicatedImage(result || null);
    }, [duplicateImage, id, isDuplicatingImage]);

    useEffect(() => {
        if (id && !isDuplicatingImage && duplicatedImage === undefined) {
            duplicateImageLoading();
        }
    }, [id, isDuplicatingImage, duplicateImage, duplicatedImage, duplicateImageLoading]);

    return {
        isDuplicatingImage,
        duplicatedImage,
        duplicateImage,
    };
};
